import { CalendarDate } from '@internationalized/date';
import {
  CurrencyCode,
  enumUnit,
  LoadLineItemInput,
  LoadStatus,
  Weight,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';

import { toWeightInput } from '../utils/weight';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

export type LoadUpdateParams = {
  id: string;
  expectedShipDate?: CalendarDate;
  description?: string;
  currencyCode?: CurrencyCode | null;
  shippingCarrierName?: string;
  scac?: string;
  freightForwarder?: string;
  truckFreight?: string;
  deliveryNumber?: string;
  releaseNumber?: string;
  driverName?: string;
  truckNumber?: string | null;
  trailerNumber?: string | null;
  containerNumber?: string;
  containerTypeId?: string;
  sealNumber?: string;
  recyclerInternalNotes?: string | null;
  recyclerExternalNotes?: string | null;
  customerInternalNotes?: string | null;
  customerExternalNotes?: string | null;
  bolNumber?: string;
  customerAccountId?: string | null;
  customerAccountAddressId?: string | null;
  carrierAccountId?: string | null;
  freightCost?: { amount: number; currencyCode: string } | null;
  freightTerms?: string | null;
  lineItems?: LoadLineItemInput[];
  deductions?: Array<Record<string, unknown>>;
  salesOrderId?: string | null;
  bookingId?: string;
  facilityId?: string;
  status?: LoadStatus;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  weightDisplayUnit?: WeightDisplayUnit | null;
  outboundTollingAgreementId?: string | null;
  receivingFacilityId?: string | null;
  expectedVersion?: number;
  failedToSaveMessage?: (ticketLabel: string) => string;
};

export const loadUpdateAtom = createMutationAtomFactory(
  'trade.loadUpdate',
  (params: LoadUpdateParams) => {
    const input: Record<string, unknown> = {};

    // Only add fields if they have values
    if (params.expectedShipDate)
      input.expectedShipDate = params.expectedShipDate.toString();
    if (params.description) input.description = params.description;
    if (params.currencyCode) input.currencyCode = params.currencyCode;
    if (params.shippingCarrierName)
      input.shippingCarrierName = params.shippingCarrierName;
    if (params.scac) input.scac = params.scac;
    if (params.freightForwarder)
      input.freightForwarder = params.freightForwarder;
    if (params.truckFreight) input.truckFreight = params.truckFreight;
    if (params.deliveryNumber) input.deliveryNumber = params.deliveryNumber;
    if (params.releaseNumber) input.releaseNumber = params.releaseNumber;
    if (params.driverName) input.driverName = params.driverName;
    if (params.truckNumber !== undefined)
      input.truckNumber = params.truckNumber;
    if (params.trailerNumber !== undefined)
      input.trailerNumber = params.trailerNumber;
    if (params.containerTypeId) input.containerTypeId = params.containerTypeId;
    if (params.containerNumber) input.containerNumber = params.containerNumber;
    if (params.sealNumber) input.sealNumber = params.sealNumber;
    if (params.recyclerInternalNotes !== undefined)
      input.recyclerInternalNotes = params.recyclerInternalNotes;
    if (params.recyclerExternalNotes !== undefined)
      input.recyclerExternalNotes = params.recyclerExternalNotes;
    if (params.customerInternalNotes !== undefined)
      input.customerInternalNotes = params.customerInternalNotes;
    if (params.customerExternalNotes !== undefined)
      input.customerExternalNotes = params.customerExternalNotes;
    if (params.bolNumber) input.bolNumber = params.bolNumber;
    if (params.customerAccountId !== undefined)
      input.customerAccountId = params.customerAccountId;
    if (params.customerAccountAddressId !== undefined)
      input.customerAccountAddressId = params.customerAccountAddressId;
    if (params.carrierAccountId !== undefined)
      input.carrierAccountId = params.carrierAccountId;
    if (params.freightCost !== undefined)
      input.freightCost = params.freightCost;
    if (params.freightTerms !== undefined)
      input.freightTerms = params.freightTerms;
    if (params.lineItems) {
      input.lineItems = params.lineItems.map((item) => ({
        ...item,
        pricingUnit: item.pricingUnit ?? enumUnit.Pounds,
      }));
    }
    if (params.deductions) {
      input.deductions = params.deductions;
    }
    if (params.salesOrderId !== undefined)
      input.salesOrderId = params.salesOrderId;
    if (params.bookingId) input.bookingId = params.bookingId;
    if (params.facilityId) input.facilityId = params.facilityId;
    if (params.status) input.status = params.status;

    // Handle weight fields - only include if explicitly provided
    if (params.heavyWeight !== undefined) {
      input.heavyWeight = toWeightInput(params.heavyWeight);
    }
    if (params.lightWeight !== undefined) {
      input.lightWeight = toWeightInput(params.lightWeight);
    }
    if (params.weightDisplayUnit) {
      input.weightDisplayUnit = params.weightDisplayUnit;
    }
    if (params.outboundTollingAgreementId !== undefined) {
      input.outboundTollingAgreementId = params.outboundTollingAgreementId;
    }
    if (params.receivingFacilityId !== undefined) {
      input.receivingFacilityId = params.receivingFacilityId;
    }
    if (params.expectedVersion !== undefined) {
      input.expectedVersion = params.expectedVersion;
    }

    return {
      tradeLoadUpdate: {
        __args: {
          id: params.id,
          input,
        },
        load: {
          id: true,
          version: true,
          status: true,
          currencyCode: true,
          expectedShipDate: true,
          description: true,
          customerAccountId: true,
          customerAccountAddressId: true,
          salesOrderId: true,
          bookingId: true,
          facilityId: true,
          shippingCarrierName: true,
          scac: true,
          freightForwarder: true,
          truckFreight: true,
          deliveryNumber: true,
          releaseNumber: true,
          driverName: true,
          truckNumber: true,
          trailerNumber: true,
          containerNumber: true,
          sealNumber: true,
          bolNumber: true,
          containerTypeId: true,
          containerType: {
            id: true,
            name: true,
          },
          heavyWeight: {
            value: true,
            unit: true,
          },
          lightWeight: {
            value: true,
            unit: true,
          },
          outboundTollingAgreementId: true,
          receivingFacilityId: true,
          lineItems: {
            id: true,
            type: true,
            itemId: true,
            tagId: true,
          },
          deductions: {
            id: true,
            deductionTypeId: true,
            applicationType: true,
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
        },
        orphanedLink: {
          bookingId: true,
          documentNumber: true,
          salesOrderId: true,
          salesOrderNumber: true,
        },
      },
    };
  },
  { showLoadingOverlay: false },
);
